import { ExpandMore, ExpandMoreRounded } from '@mui/icons-material';
import { alpha, darken, Theme } from '@mui/material';
import type {} from '@mui/x-data-grid/themeAugmentation';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  CheckboxCheckedIcon,
  CheckboxIndeterminateIcon,
  CheckboxUncheckedIcon,
  RadioCheckedIcon,
  RadioUncheckedIcon,
} from '@shared/custom-icons';
import { DataGridClass, TabsClass } from './classes';

// Palette reads go through `paletteOf(theme)`: CSS variables that follow the active colour scheme
// when the theme provides them (mgpris, kjedeadmin), plain values otherwise (DAP). Style callbacks
// are typed without `vars`, hence the narrower ColorTheme. `alpha()`/`darken()` cannot take a CSS
// variable, so derived colours go through `translucent`/`darkened`.
type ColorTheme = Pick<Theme, 'palette'> & Partial<Pick<Theme, 'vars'>>;

const paletteOf = (theme: ColorTheme) => (theme.vars || theme).palette;

const translucent = (theme: ColorTheme, color: string, opacity: number) =>
  theme.vars
    ? `color-mix(in srgb, ${color} ${opacity * 100}%, transparent)`
    : alpha(color, opacity);

const darkened = (theme: ColorTheme, color: string, coefficient: number) =>
  theme.vars
    ? `color-mix(in srgb, ${color} ${(1 - coefficient) * 100}%, #000)`
    : darken(color, coefficient);

const dataGridSurface = (theme: ColorTheme) => paletteOf(theme).background.paper;

const dataGridStripe = (theme: ColorTheme) =>
  theme.palette.mode === 'dark' ? paletteOf(theme).grey[800] : paletteOf(theme).info[100];

const dataGridPinnedStripe = (theme: ColorTheme) =>
  theme.palette.mode === 'dark' ? paletteOf(theme).grey[800] : '#f0f9ff';

const dataGridHighlight = (theme: ColorTheme) =>
  theme.palette.mode === 'dark'
    ? translucent(theme, paletteOf(theme).primary.main, 0.32)
    : paletteOf(theme).primary[100];

const dataGridBorder = (theme: ColorTheme) =>
  theme.palette.mode === 'dark' ? paletteOf(theme).grey[500] : paletteOf(theme).divider;

const listHighlight = (theme: ColorTheme, kind: 'selected' | 'hover') => {
  const palette = paletteOf(theme);
  if (theme.palette.mode === 'dark') {
    const color = translucent(theme, palette.primary.main, kind === 'selected' ? 0.28 : 0.16);
    return kind === 'hover' ? `${color} !important` : color;
  }

  return kind === 'selected' ? palette.primary[50] : `${palette.primary.A100} !important`;
};

export const componentsStyles: Theme['components'] = {
  MuiAppBar: {
    defaultProps: {
      elevation: 0,
    },
  },
  MuiPaper: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      rounded: ({ theme }) => ({
        borderRadius: Number(theme.shape.borderRadius) * 3,
      }),
    },
  },
  MuiButtonBase: {
    styleOverrides: {
      root: {
        fontFamily: 'inherit',
      },
    },
    defaultProps: {
      disableRipple: true,
      disableTouchRipple: true,
    },
  },
  MuiButton: {
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        borderWidth: ownerState.size === 'large' ? 2 : 1,
        borderStyle: 'solid',
        borderRadius: Number(theme.shape.borderRadius) * 3,
        textTransform: 'none',
        strokeWidth: 2,
        fontWeight: 500,

        '&:hover': {
          borderWidth: ownerState.size === 'large' ? 2 : 1,
          borderStyle: 'solid',
          boxShadow: theme.shadows[1],
          transition: 'none', // Remove for smoother border transition
          borderColor: paletteOf(theme).grey[900],
          color: paletteOf(theme).common.white,
          background: paletteOf(theme).grey[900],
        },
        '&:focus-visible': {
          boxShadow: theme.shadows[1],
          outline: `1px solid ${paletteOf(theme).grey[900]}`,
          outlineOffset: 3,
        },
        '&:disabled': {
          borderWidth: ownerState.size === 'large' ? 2 : 1,
          borderStyle: 'solid',
          background:
            theme.palette.mode === 'dark' ? paletteOf(theme).grey[800] : paletteOf(theme).grey.A100,
          color:
            theme.palette.mode === 'dark'
              ? paletteOf(theme).grey[400]
              : paletteOf(theme).text.disabled,
          borderColor:
            theme.palette.mode === 'dark' ? paletteOf(theme).grey[700] : paletteOf(theme).grey.A100,
        },
      }),
      sizeSmall: ({ theme }) => ({
        ...theme.typography.body2,
        fontWeight: 500,
        padding: '9px 16px',
      }),
      sizeLarge: ({ theme }) => ({
        ...theme.typography.body1,
        fontWeight: 500,
        padding: '10px 38px',
      }),
      containedPrimary: ({ theme }) => ({
        color:
          theme.palette.mode === 'dark'
            ? paletteOf(theme).primary.contrastText
            : paletteOf(theme).common.white,
        background:
          theme.palette.mode === 'dark'
            ? paletteOf(theme).primary.main
            : paletteOf(theme).gradient.primary,
        transition: 'none', // Remove for smoother border transition
        // Compensate with padding for no border color, cannot use gradient border color
        border: 'none',
        '&.MuiButton-sizeSmall': {
          padding: '10px 17px',
          ':hover, :disabled': {
            border: 0,
            padding: '10px 17px',
          },
        },
        '&.MuiButton-sizeLarge': {
          padding: '12px 40px',
          ':hover, :disabled': {
            border: 0,
            padding: '12px 40px',
          },
        },
      }),
      containedSecondary: ({ theme }) => ({
        color:
          theme.palette.mode === 'dark'
            ? paletteOf(theme).secondary.contrastText
            : paletteOf(theme).text.primary,
        background:
          theme.palette.mode === 'dark'
            ? paletteOf(theme).secondary.main
            : paletteOf(theme).gradient.secondary,
        borderColor: 'textSecondary',
        transition: 'none', // Remove for smoother border transition
      }),
      containedWarning: ({ theme }) => ({
        color:
          theme.palette.mode === 'dark'
            ? paletteOf(theme).warning.contrastText
            : paletteOf(theme).text.primary,
        background: paletteOf(theme).warning.main,
        borderColor: paletteOf(theme).warning.main,
      }),
      outlined: {
        '&.MuiButton-sizeSmall': {
          padding: '9px 16px',
          ':hover, :disabled': {
            padding: '9px 16px',
          },
        },
      },
      outlinedPrimary: ({ theme }) => ({
        color:
          theme.palette.mode === 'dark'
            ? paletteOf(theme).primary.main
            : paletteOf(theme).gradient.primary,
        background:
          theme.palette.mode === 'dark'
            ? 'transparent'
            : `linear-gradient(${paletteOf(theme).background.paper}, ${
                paletteOf(theme).background.paper
              }) padding-box, ${paletteOf(theme).gradient.primary} border-box`,
        borderColor: theme.palette.mode === 'dark' ? paletteOf(theme).primary.main : undefined,
        transition: 'none', // Remove for smoother border transition
      }),
      outlinedSecondary: ({ theme }) => ({
        color:
          theme.palette.mode === 'dark'
            ? paletteOf(theme).secondary.main
            : paletteOf(theme).text.secondary,
        background:
          theme.palette.mode === 'dark'
            ? 'transparent'
            : `linear-gradient(${paletteOf(theme).background.paper}, ${
                paletteOf(theme).background.paper
              }) padding-box, ${paletteOf(theme).gradient.secondary} border-box`,
        borderColor: theme.palette.mode === 'dark' ? paletteOf(theme).secondary.main : undefined,
        transition: 'none', // Remove for smoother border transition
      }),
      outlinedWarning: ({ theme }) => ({
        color:
          theme.palette.mode === 'dark'
            ? paletteOf(theme).warning.main
            : paletteOf(theme).text.secondary,
        background:
          theme.palette.mode === 'dark' ? 'transparent' : paletteOf(theme).background.paper,
        borderColor: paletteOf(theme).warning.main,
      }),
      text: () => ({
        borderColor: 'transparent',
        transition: 'none', // Remove for smoother border transition
      }),
    },
    variants: [
      {
        props: { color: 'grey', variant: 'outlined' },
        style: ({ theme }) => ({
          color:
            theme.palette.mode === 'dark'
              ? paletteOf(theme).text.primary
              : paletteOf(theme).text.secondary,
          background:
            theme.palette.mode === 'dark'
              ? paletteOf(theme).grey[800]
              : paletteOf(theme).background.paper,
          borderColor:
            theme.palette.mode === 'dark' ? paletteOf(theme).grey[500] : paletteOf(theme).grey.A100,
        }),
      },
      {
        props: { color: 'grey', variant: 'contained' },
        style: ({ theme }) => ({
          color:
            theme.palette.mode === 'dark'
              ? paletteOf(theme).text.primary
              : paletteOf(theme).text.secondary,
          background:
            theme.palette.mode === 'dark'
              ? paletteOf(theme).grey[800]
              : paletteOf(theme).background.paper,
          borderColor:
            theme.palette.mode === 'dark'
              ? paletteOf(theme).grey[500]
              : paletteOf(theme).background.paper,
        }),
      },
    ],
  },
  MuiToggleButton: {
    defaultProps: {
      disableFocusRipple: true,
      disableRipple: true,
    },
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        borderWidth: ownerState.size === 'large' ? 2 : 1,
        borderStyle: 'solid',
        borderRadius: Number(theme.shape.borderRadius) * 3,
        textTransform: 'none',
        color: paletteOf(theme).text.primary,
        background:
          theme.palette.mode === 'dark'
            ? paletteOf(theme).grey[800]
            : paletteOf(theme).background.paper,
        '&.Mui-selected': {
          borderColor: paletteOf(theme).primary.main,
          background:
            theme.palette.mode === 'dark'
              ? paletteOf(theme).grey[800]
              : paletteOf(theme).background.paper,
        },
        '&:hover, &.Mui-selected:hover': {
          borderWidth: ownerState.size === 'large' ? 2 : 1,
          borderStyle: 'solid',
          boxShadow: theme.shadows[1],
          transition: 'none', // Remove for smoother border transition
          borderColor: paletteOf(theme).grey[900],
          color: paletteOf(theme).common.white,
          background: paletteOf(theme).grey[900],
        },
        '&:focus-visible': {
          boxShadow: theme.shadows[1],
        },
        '&:disabled': {
          borderWidth: ownerState.size === 'large' ? 2 : 1,
          borderStyle: 'solid',
          background:
            theme.palette.mode === 'dark' ? paletteOf(theme).grey[800] : paletteOf(theme).grey.A100,
          color:
            theme.palette.mode === 'dark'
              ? paletteOf(theme).grey[400]
              : paletteOf(theme).text.disabled,
          borderColor:
            theme.palette.mode === 'dark' ? paletteOf(theme).grey[700] : paletteOf(theme).grey.A100,
        },
      }),
      sizeSmall: ({ theme }) => ({
        ...theme.typography.body2,
        fontWeight: 500,
        padding: '7.5px',
      }),
      sizeLarge: ({ theme }) => ({
        ...theme.typography.body1,
        fontWeight: 500,
        padding: '10px',
      }),

      selected: ({ theme }) => ({
        borderColor: paletteOf(theme).primary.main,
        background:
          theme.palette.mode === 'dark'
            ? paletteOf(theme).grey[800]
            : paletteOf(theme).background.paper,
      }),
    },
    // css state classes didnt work for primary, secondary, standard
    variants: [
      {
        props: { color: 'primary' },
        style: ({ theme }) => ({
          color: paletteOf(theme).primary.main,
          '&.Mui-selected': {
            borderColor: paletteOf(theme).primary.main,
          },
        }),
      },
      {
        props: { color: 'secondary' },
        style: ({ theme }) => ({
          color: paletteOf(theme).secondary.main,
          '&.Mui-selected': {
            borderColor: paletteOf(theme).secondary.main,
          },
        }),
      },
      {
        props: { color: 'warning' },
        style: ({ theme }) => ({
          color: paletteOf(theme).warning.main,
          '&.Mui-selected': {
            borderColor: paletteOf(theme).warning.main,
          },
        }),
      },
      {
        props: { color: 'success' },
        style: ({ theme }) => ({
          color: paletteOf(theme).success.main,
          '&.Mui-selected': {
            borderColor: paletteOf(theme).success.main,
          },
        }),
      },
    ],
  },
  MuiCard: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: Number(theme.shape.borderRadius) * 3,
      }),
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(2, 2, 0, 2),
      }),
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&:last-child': { paddingBottom: theme.spacing(2) },
      }),
    },
  },
  MuiCardActions: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(0, 2, 2, 2),
      }),
    },
  },
  MuiPopover: {
    defaultProps: {
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left',
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'left',
      },
    },
    styleOverrides: {
      paper: ({ theme }) => ({
        boxShadow: theme.shadows[2],
        padding: theme.spacing(2.5),
        borderRadius: theme.spacing(1),
        border: `1px solid ${paletteOf(theme).grey.A100}`,
      }),
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        ':hover': {
          color: paletteOf(theme).primary.main,
        },
      }),
    },
  },
  MuiPaginationItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: paletteOf(theme).grey[500],
        ...theme.typography.body2,
        fontWeight: 600,
      }),
      rounded: ({ theme }) => ({
        borderRadius: Number(theme.shape.borderRadius) * 2,
      }),
      page: ({ theme }) => ({
        backgroundColor:
          theme.palette.mode === 'dark' ? paletteOf(theme).grey[800] : paletteOf(theme).grey.A100,
        color:
          theme.palette.mode === 'dark'
            ? paletteOf(theme).text.primary
            : paletteOf(theme).grey[500],
        margin: theme.spacing(0, 0.5),
        border: '1px solid transparent',

        '&:hover': {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? translucent(theme, paletteOf(theme).primary.main, 0.24)
              : paletteOf(theme).primary.A100,
          borderColor:
            theme.palette.mode === 'dark'
              ? paletteOf(theme).grey[500]
              : paletteOf(theme).primary['50'],
          color: paletteOf(theme).text.primary,
          boxShadow: theme.shadows[1],
        },

        '&.Mui-selected': {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? paletteOf(theme).primary.main
              : paletteOf(theme).warning['500'],
          color:
            theme.palette.mode === 'dark'
              ? paletteOf(theme).primary.contrastText
              : paletteOf(theme).text.primary,

          '&:hover': {
            backgroundColor:
              theme.palette.mode === 'dark'
                ? paletteOf(theme).primary.main
                : paletteOf(theme).primary.A100,
            borderColor:
              theme.palette.mode === 'dark'
                ? paletteOf(theme).grey[500]
                : paletteOf(theme).primary['50'],
            color:
              theme.palette.mode === 'dark'
                ? paletteOf(theme).primary.contrastText
                : paletteOf(theme).text.primary,
            boxShadow: theme.shadows[1],
          },
        },
      }),
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: ({ theme }) => ({
        marginRight: theme.spacing(2),
        '& .MuiTabs-indicator': {
          display: 'none',
        },

        [`&.${TabsClass.underlined}`]: {
          width: '100%',
          marginRight: 0,
          paddingLeft: theme.spacing(3),
          paddingRight: theme.spacing(3),
          paddingTop: theme.spacing(2),
          borderBottom: `1px solid ${paletteOf(theme).grey.A100}`,

          '& .MuiTab-root': {
            paddingBottom: theme.spacing(1.5),
          },
          '& .MuiTabs-flexContainer': {
            justifyContent: 'space-between',
          },

          '& .MuiTabs-indicator': {
            display: 'block',
            background: paletteOf(theme).gradient.primary,
            height: '4px',
            borderRadius: Number(theme.shape.borderRadius) * 5,
          },
        },
      }),
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        border: `2px solid ${
          theme.palette.mode === 'dark' ? paletteOf(theme).grey[500] : paletteOf(theme).divider
        }`,
        backgroundColor:
          theme.palette.mode === 'dark'
            ? paletteOf(theme).grey[900]
            : translucent(theme, paletteOf(theme).background.paper, 0.5),
        borderTopLeftRadius: Number(theme.shape.borderRadius) * 3,
        borderTopRightRadius: Number(theme.shape.borderRadius) * 3,
        textTransform: 'none',
        fontSize: theme.typography.body1.fontSize,
        fontWeight: 700,
        color: paletteOf(theme).text.secondary,
        padding: theme.spacing(1, 2),
        minHeight: theme.spacing(6),

        [`&:first-of-type:not(.${TabsClass.underlined})`]: {
          '&.Mui-selected': {
            border: 0,
            boxShadow: `-2px 2px 0 0 ${
              paletteOf(theme).divider
            } inset, 6px 8px 8px rgba(15, 15, 15, 0.2)`,
            padding: `${theme.spacing(1)} calc(${theme.spacing(2)} + 2px)`,
          },
        },

        [`&:not(:first-of-type):not(.${TabsClass.underlined})`]: {
          marginLeft: '-2px', // overlap borders
        },

        [`&.Mui-selected:not(.${TabsClass.underlined})`]: {
          boxShadow: '6px 8px 8px rgba(15, 15, 15, 0.2)',
          backgroundColor:
            theme.palette.mode === 'dark'
              ? paletteOf(theme).grey[800]
              : paletteOf(theme).background.paper,
          color: paletteOf(theme).text.primary,
          borderBottom: 0,
        },

        [`&.${TabsClass.underlined}`]: {
          border: 0,
          background: 'none',
          borderRadius: 0,
          padding: theme.spacing(1),
          fontSize: theme.typography.body1.fontSize,
          fontWeight: 500,
          minHeight: '45px',
          transition: theme.transitions.create(['all'], {
            duration: theme.transitions.duration.short,
          }),

          '&.Mui-selected': {
            boxShadow: 'none',
            backgroundColor: 'transparent',
            color: paletteOf(theme).text.primary,
            fontWeight: 700,
          },

          '&.Mui-focusVisible': {
            borderRadius: Number(theme.shape.borderRadius) * 2,
            background: paletteOf(theme).primary.A200,
          },
        },
      }),
    },
  },
  MuiDataGrid: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: dataGridSurface(theme),
        display: 'grid',
        gridTemplateRows: 'auto 1fr auto',

        borderWidth: '0 0 1px 0',
        '--DataGrid-containerBackground': dataGridSurface(theme),

        '.MuiDataGrid-filler': {
          backgroundColor: dataGridSurface(theme),
        },

        // Treedata
        [`&.${DataGridClass.treeDataTable}`]: {
          '& .MuiDataGrid-main': {
            border: `1px solid ${
              theme.palette.mode === 'dark'
                ? paletteOf(theme).grey[500]
                : paletteOf(theme).grey.A100
            }`,
            borderRadius: theme.spacing(1),

            '& .MuiDataGrid-columnHeaders': {
              padding: theme.spacing(0.5),
              background: dataGridSurface(theme),
            },
          },
        },
      }),
      virtualScroller: {
        minHeight: 100, // set a min height to show no rows text
      },
      filler: ({ theme }) => ({
        backgroundColor: dataGridSurface(theme),
      }),
      withBorderColor: ({ theme }) => ({
        borderColor: dataGridBorder(theme),
      }),
      columnHeaders: ({ theme }) => ({
        borderBottom: `1px solid ${dataGridBorder(theme)}`,
        color: paletteOf(theme).text.primary,
      }),
      columnHeader: ({ theme }) => ({
        background: dataGridSurface(theme),

        '&:focus-within': {
          outline: 'none',
        },

        '&:not(.MuiDataGrid-columnHeader--sorted):hover .MuiDataGrid-sortIcon': {
          opacity: 1,
        },
      }),
      columnHeaderTitle: ({ theme }) => ({
        ...theme.typography.body1,
        fontWeight: 700,
        color: paletteOf(theme).text.primary,
      }),
      columnHeaderCheckbox: () => ({
        padding: 0,
      }),
      row: ({ theme }) => ({
        // Alternating row background
        ':nth-of-type(odd)': {
          backgroundColor: dataGridStripe(theme),

          '> .MuiDataGrid-cell--pinnedRight, .MuiDataGrid-cell--pinnedLeft': {
            backgroundColor: dataGridPinnedStripe(theme),
          },
        },
        ':nth-of-type(even)': {
          backgroundColor: 'inherit',

          '> .MuiDataGrid-cell--pinnedRight, .MuiDataGrid-cell--pinnedLeft': {
            backgroundColor: dataGridSurface(theme),
          },
        },

        // On row hover
        '&:hover': {
          backgroundColor: `${dataGridHighlight(theme)} !important`,

          '.MuiDataGrid-cell--pinnedRight': {
            backgroundColor: `${dataGridHighlight(theme)} !important`,
          },

          '.MuiDataGrid-cell--pinnedLeft': {
            backgroundColor: `${dataGridHighlight(theme)} !important`,
          },

          // Styling for making links and icons visible while hovering on a row
          [`& .${DataGridClass.cellIcon}`]: {
            opacity: 1,
          },
          [`& .${DataGridClass.cellLink}`]: {
            color:
              theme.palette.mode === 'dark'
                ? paletteOf(theme).primary.light
                : paletteOf(theme).primary.main,
            textDecoration: 'underline',
          },
        },

        // Selected rows
        [`&.Mui-selected`]: {
          backgroundColor: `${dataGridHighlight(theme)} !important`,
          borderBottom: `1px solid ${dataGridBorder(theme)}`,

          '.MuiDataGrid-cell--pinnedRight': {
            backgroundColor: `${dataGridHighlight(theme)} !important`,
          },

          '.MuiDataGrid-cell--pinnedLeft': {
            backgroundColor: `${dataGridHighlight(theme)} !important`,
          },
        },

        // Clickable rows (onRowClick provided)
        [`&.${DataGridClass.cellClickable}`]: {
          '&:hover': {
            backgroundColor: `${dataGridHighlight(theme)} !important`,
            cursor: 'pointer',
          },
        },

        // Links and icons
        [`& .${DataGridClass.cellIcon}`]: {
          opacity: 0,
        },
        [`& .${DataGridClass.cellLink}`]: {
          color: 'inherit',
          textDecoration: 'none',
        },

        // Treedata
        [`&.${DataGridClass.treeParentRow}`]: {
          background: 'inherit',

          '& .MuiDataGrid-treeDataGroupingCellToggle': {
            marginRight: theme.spacing(1),
          },
        },

        [`&.${DataGridClass.treeChildRow}`]: {
          '& .MuiDataGrid-treeDataGroupingCellToggle': {
            marginRight: theme.spacing(-1),
          },
        },
      }),
      cell: ({ theme }) => ({
        ...theme.typography.body2,
        color: paletteOf(theme).text.secondary,
        fontWeight: 600,
        whiteSpace: 'normal !important',
        display: 'flex',
        alignItems: 'center',
        alignContent: 'center',
        overflow: 'hidden',
        textOverflow: 'ellipsis',

        [`&.${DataGridClass.cellBold}`]: {
          fontWeight: 700,
          color: paletteOf(theme).text.primary,
        },

        '&--pinnedRight': {
          boxShadow: '-2px 0px 4px -2px rgba(0, 0, 0, 0.21)',
        },

        '&--pinnedLeft': {
          boxShadow: '2px 0px 4px -2px rgba(0, 0, 0, 0.21)',
        },

        // Highlight editable cells
        '&--editable': {
          border: `1px solid ${paletteOf(theme).info[500]}`,
        },
      }),
      panel: ({ theme }) => ({
        '& .MuiPaper-root': {
          boxShadow: theme.shadows[2],
          padding: theme.spacing(2),
          borderRadius: theme.spacing(1),
          border: `1px solid ${paletteOf(theme).divider}`,

          '.MuiDataGrid-columnsManagementHeader': {
            padding: theme.spacing(1),
          },

          '.MuiDataGrid-columnsManagement': {
            padding: theme.spacing(1, 2),
          },

          '.MuiDataGrid-columnsManagementFooter': {
            padding: theme.spacing(1, 2, 0),
          },
        },
      }),
      menu: ({ theme }) => ({
        '& .MuiPaper-root': {
          boxShadow: theme.shadows[2],
          padding: theme.spacing(0.5, 1),
          borderRadius: theme.spacing(1),
          border: `1px solid ${paletteOf(theme).divider}`,
        },
      }),
      toolbarContainer: ({ theme }) => ({
        '.MuiFormControl-root': {
          paddingBottom: 0,
        },

        '& .MuiFormControl-root .MuiInputBase-root': {
          margin: 0,
          height: 'fit-content',
          background:
            theme.palette.mode === 'dark'
              ? paletteOf(theme).background.paper
              : paletteOf(theme).primary.A100,
          borderRadius: Number(theme.shape.borderRadius) * 2,

          '& input': {
            '::placeholder': {
              color: paletteOf(theme).text.secondary,
              ...theme.typography.body2,
              fontWeight: 450,
            },
          },

          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              border: `1px solid ${
                theme.palette.mode === 'dark'
                  ? paletteOf(theme).grey[500]
                  : paletteOf(theme).grey.A100
              }`,
              borderRadius: Number(theme.shape.borderRadius) * 2,
            },
          },

          '.MuiSvgIcon-root': {
            fill: paletteOf(theme).action.active,
          },
        },
      }),
      footerContainer: ({ theme }) => ({
        ...theme.typography.body2,
        minHeight: '10px',
        paddingTop: 20,
        borderTop: `1px solid ${paletteOf(theme).divider}`,
      }),
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => ({
        padding: theme.spacing(4),
        borderRadius: theme.spacing(3),
        boxShadow: theme.shadows[1],
        position: 'relative',
        overflowY: 'unset',
      }),
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(0, 0, 2),
      }),
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(0),
        margin: theme.spacing(0, 0, 2),

        ':last-child': {
          paddingBottom: 0,
        },
      }),
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: () => ({
        padding: 0,
      }),
    },
  },
  MuiAccordion: {
    styleOverrides: {
      root: ({ theme }) => ({
        background: paletteOf(theme).primary.A100,
        '&::before': { height: 0 },
        '&.active': {
          '> .MuiAccordionSummary-root': {
            color: paletteOf(theme).primary.main,
          },
        },
      }),
      rounded: ({ theme }) => ({
        '&.MuiPaper-root': {
          borderRadius: theme.spacing(1.5),
        },
      }),
    },
    defaultProps: {
      disableGutters: true,
      TransitionProps: { unmountOnExit: true, mountOnEnter: true },
      elevation: 0,
    },
  },
  MuiAccordionSummary: {
    defaultProps: {
      expandIcon: <ExpandMore />,
    },
    styleOverrides: {
      root: {
        fontWeight: 700,
      },
    },
  },
  MuiCheckbox: {
    defaultProps: {
      checkedIcon: <CheckboxCheckedIcon />,
      icon: <CheckboxUncheckedIcon />,
      indeterminateIcon: <CheckboxIndeterminateIcon />,
    },
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        ...(ownerState.size === 'small'
          ? {
              width: theme.spacing(4.25),
              height: theme.spacing(4.25),
            }
          : {}),
        // disabled-class didnt work...
        '&.Mui-disabled': {
          opacity: '0.5',
        },
        '&.Mui-focusVisible': {
          background:
            theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.16)'
              : `rgba(from ${paletteOf(theme).common.black} r g b / 0.15)`,
        },
      }),
    },
  },
  MuiRadio: {
    defaultProps: {
      checkedIcon: <RadioCheckedIcon />,
      icon: <RadioUncheckedIcon />,
    },
    styleOverrides: {
      root: ({ ownerState, theme: { spacing } }) => ({
        ...(ownerState.size === 'small'
          ? {
              width: spacing(4.25),
              height: spacing(4.25),
            }
          : {}),
        // disabled-class didnt work...
        '&.Mui-disabled': {
          opacity: '0.5',
        },
      }),
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: ({ theme: { spacing } }) => ({
        gap: spacing(1),
      }),
      grouped: ({ theme }) => ({
        border: `2px solid ${paletteOf(theme).grey.A100} !important`,
        color: paletteOf(theme).text.primary,
        '&.MuiToggleButtonGroup-grouped': {
          borderRadius: Number(theme.shape.borderRadius) * 2,
          '&:hover': {
            color: paletteOf(theme).common.white,
            border: `2px solid ${paletteOf(theme).grey[900]} !important`,
            transition: 'none', // Remove for smoother border transition
          },
        },
        '&:hover': {
          transition: 'none', // Remove for smoother border transition
        },

        '&.Mui-selected': {
          '&.MuiToggleButton-primary, &.MuiToggleButton-standard': {
            borderColor: `${paletteOf(theme).primary.main} !important`,
            color: paletteOf(theme).primary.main,
          },
          '&.MuiToggleButton-secondary': {
            borderColor: `${paletteOf(theme).secondary.main} !important`,
            color: paletteOf(theme).secondary.main,
          },
          '&.MuiToggleButton-warning': {
            borderColor: `${paletteOf(theme).warning.main} !important`,
            color: paletteOf(theme).warning.main,
          },
          '&.MuiToggleButton-success': {
            borderColor: `${paletteOf(theme).success.main} !important`,
            color: paletteOf(theme).success.main,
          },
          '&:hover': {
            color: paletteOf(theme).common.white,
            borderWidth: '2px',
            borderStyle: 'solid',
            transition: 'none', // Remove for smoother border transition
          },
        },
        '&.MuiToggleButton-sizeSmall': {
          padding: theme.spacing(0.5),
        },
        '&.MuiToggleButton-sizeMedium': {
          padding: theme.spacing(1),
        },
      }),
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      notchedOutline: ({ ownerState, theme }) => ({
        borderWidth: `${ownerState.size === 'medium' ? 2 : 1}px `,
        borderColor:
          theme.palette.mode === 'dark' ? paletteOf(theme).grey[500] : paletteOf(theme).grey.A100,
        transition: theme.transitions.create(['border-color']),
      }),

      inputSizeSmall: {
        minHeight: '23px', //compensate for 14px font size
      },

      root: ({ ownerState, theme }) => ({
        borderRadius:
          ownerState.size === 'medium'
            ? Number(theme.shape.borderRadius) * 3
            : Number(theme.shape.borderRadius) * 2,
        background:
          theme.palette.mode === 'dark'
            ? paletteOf(theme).grey[800]
            : paletteOf(theme).background.paper,
        color: paletteOf(theme).text.primary,
        ...(ownerState.size === 'medium' ? theme.typography.body1 : theme.typography.body2),
        fontWeight: 500,

        '& input': {
          '::placeholder':
            theme.palette.mode === 'dark'
              ? {
                  color: paletteOf(theme).text.secondary,
                  opacity: 1,
                }
              : {
                  opacity: 0.6,
                },
        },

        // Hover
        [`&:hover .MuiOutlinedInput-notchedOutline`]: {
          borderColor: paletteOf(theme).primary.main,
        },

        // Focus state
        [`&.Mui-focused`]: {
          background:
            theme.palette.mode === 'dark'
              ? translucent(theme, paletteOf(theme).primary.main, 0.16)
              : paletteOf(theme).primary.A100,

          '.MuiOutlinedInput-notchedOutline': {
            borderColor: paletteOf(theme).primary.main,
            borderWidth: `${ownerState.size === 'medium' ? 2 : 1}px `,
          },
        },

        // Error state
        [`&.Mui-error`]: {
          color:
            theme.palette.mode === 'dark'
              ? paletteOf(theme).text.primary
              : paletteOf(theme).warning[900],
          background:
            theme.palette.mode === 'dark'
              ? translucent(theme, paletteOf(theme).error.main, 0.16)
              : paletteOf(theme).warning[50],

          ' svg': {
            color:
              theme.palette.mode === 'dark'
                ? paletteOf(theme).error.light
                : paletteOf(theme).warning[900],
          },

          '.MuiOutlinedInput-notchedOutline': {
            borderColor:
              theme.palette.mode === 'dark'
                ? paletteOf(theme).error.light
                : paletteOf(theme).warning[900],
          },
        },

        // Disabled state
        [`&.Mui-disabled`]: {
          background:
            theme.palette.mode === 'dark'
              ? paletteOf(theme).action.disabledBackground
              : translucent(theme, paletteOf(theme).grey.A100, 0.2),

          '.MuiOutlinedInput-notchedOutline': {
            color: paletteOf(theme).text.disabled,
            borderColor:
              theme.palette.mode === 'dark'
                ? paletteOf(theme).grey[700]
                : paletteOf(theme).grey.A100,
          },

          ' svg': {
            color: paletteOf(theme).grey[200],
          },
        },
      }),
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.body1,
        color: paletteOf(theme).text.primary,
        fontWeight: 700,
        verticalAlign: 'middle',

        '.MuiFormLabel-asterisk': {
          color:
            theme.palette.mode === 'dark'
              ? paletteOf(theme).error.main
              : paletteOf(theme).warning[900],
        },

        [`&.Mui-error`]: {
          color:
            theme.palette.mode === 'dark'
              ? paletteOf(theme).error.main
              : paletteOf(theme).warning[900],
        },

        [`&.Mui-disabled`]: {
          color: paletteOf(theme).grey[200],
        },
      }),
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: ({ theme }) => ({
        marginLeft: 0,
        marginTop: theme.spacing(1),
        ...theme.typography.body2,
        fontWeight: 500,

        [`&.Mui-error`]: {
          color:
            theme.palette.mode === 'dark'
              ? paletteOf(theme).error.main
              : paletteOf(theme).warning[900],

          ' svg': {
            color:
              theme.palette.mode === 'dark'
                ? paletteOf(theme).error.main
                : paletteOf(theme).warning[900],
          },
        },

        [`&.Mui-disabled`]: {
          color: paletteOf(theme).grey[200],

          ' svg': {
            color: paletteOf(theme).grey[200],
          },
        },
      }),
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: ({ theme }) => ({
        background: paletteOf(theme).grey[900],
        color: paletteOf(theme).common.white,
        border: `1px solid ${paletteOf(theme).grey[900]}`,
        borderRadius: Number(theme.shape.borderRadius) * 3,
        padding: theme.spacing(2),
        ...theme.typography.body3,
        boxShadow: theme.shadows[1],
      }),
      arrow: ({ theme }) => ({
        color: paletteOf(theme).grey[900],
      }),
    },
  },
  MuiBadge: {
    styleOverrides: {
      colorPrimary: ({ theme }) => ({
        background: paletteOf(theme).gradient.primary,
      }),
      colorSecondary: ({ theme }) => ({
        background: paletteOf(theme).gradient.secondary,
      }),
      colorWarning: ({ theme }) => ({
        background: paletteOf(theme).gradient.warning,
      }),
    },
  },
  MuiStepper: {
    styleOverrides: {
      horizontal: ({ theme }) => ({
        justifyContent: 'space-between',
        '.MuiStep-root': {
          padding: theme.spacing(0, 2.25),
          flex: 'none',

          ':first-of-type': {
            paddingLeft: 0,
          },

          ':last-of-type': {
            paddingRight: 0,
          },
        },

        '.MuiStepConnector-root': {
          position: 'inherit',
          flex: 1,
          alignSelf: 'center',
          '&.Mui-active, &.Mui-completed': {
            '.MuiStepConnector-line': {
              borderColor: paletteOf(theme).primary.main,
            },
          },
          '.MuiStepConnector-line': {
            borderTopWidth: '2px',
            borderColor: paletteOf(theme).grey[400],
            minWidth: '10px',
          },
        },

        '.MuiStepLabel-root': {
          padding: theme.spacing(1, 2),
          overflow: 'hidden',
          border: `2px solid transparent`,
          borderRadius: Number(theme.shape.borderRadius) * 3,
          backgroundColor: translucent(theme, paletteOf(theme).grey.A100, 0.3),

          ' svg': {
            width: '16px',
            height: '16px',
            fill: paletteOf(theme).grey[400],
          },

          '.MuiStepLabel-label': {
            color: paletteOf(theme).grey[400],
            ...theme.typography.body2,
            fontWeight: 700,
            marginTop: theme.spacing(1),
          },

          '&.Mui-active, &.Mui-completed': {
            ' svg': {
              fill: paletteOf(theme).primary.main,
            },
            borderColor: paletteOf(theme).primary.main,
            '.MuiStepLabel-label': {
              color: paletteOf(theme).primary.main,
            },
          },

          '&.Mui-completed': {
            backgroundColor: translucent(theme, paletteOf(theme).grey.A100, 0.3),
            border: `2px solid transparent`,
          },

          '&.Mui-active': {
            background: paletteOf(theme).background.paper,
          },
        },
      }),
    },
  },
  MuiSelect: {
    defaultProps: {
      IconComponent: ExpandMoreRounded,
    },
    styleOverrides: {
      select: () => ({
        minWidth: '100px',
      }),
      root: ({ ownerState }) => ({
        '.MuiOutlinedInput-input': {
          padding: '16px 14px', // compensate for 2px border
        },

        '.MuiInputBase-inputSizeSmall': {
          padding: '8.5px 14px', // compensate for 14px font size
          minHeight: '23px',
        },
      }),
    },
  },
  MuiMenu: {
    styleOverrides: {
      root: ({ theme }) => ({
        '& .MuiMenu-list': {
          padding: 0,

          '.MuiListSubheader-root': {
            ...theme.typography.body2,
            fontWeight: 700,
            color: paletteOf(theme).primary.main,
            padding: theme.spacing(0, 1, 1),
          },
        },
      }),
      paper: ({ theme }) => ({
        padding: theme.spacing(2),
        borderRadius: Number(theme.shape.borderRadius) * 3,
      }),
      list: {
        padding: 0,
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(1) + '!important',
        fontWeight: 500,
        borderRadius: Number(theme.shape.borderRadius) * 3,
        '&.Mui-selected': {
          background: listHighlight(theme, 'selected'),
          '&.Mui-focusVisible': {
            background: listHighlight(theme, 'selected'),
          },
        },
        '&:hover, &:focus, &.Mui-focused': {
          background: listHighlight(theme, 'hover'),
        },
        '&.Mui-disabled': {
          background: 'transparent',
        },
      }),
    },
  },
  MuiSwitch: {
    styleOverrides: {
      // for the internal base (/thumb in relation to the track)
      switchBase: {
        padding: 0,
        margin: 5,
        opacity: 1,
        // check state for track
        '&.Mui-checked': {
          '& + .MuiSwitch-track': {
            opacity: 1,
          },
        },

        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.6,
        },
      },
      // unchecked state
      track: ({ theme }) => ({
        background: paletteOf(theme).grey['400'],
        opacity: 1,

        border: 0,
        borderRadius: 30 / 2,
      }),
      thumb: ({ theme }) => ({
        background: paletteOf(theme).common.white,

        width: 20,
        height: 20,
        boxSizing: 'border-box',
      }),
      sizeSmall: ({ theme }) => ({
        height: 25,
        width: 41,
        padding: 0,
        '& .MuiSwitch-switchBase': {
          padding: 0,
          margin: 3.5,
          opacity: 1,
          background: 'red',

          '&.Mui-checked': {
            '& + .MuiSwitch-track': {
              opacity: 1,
            },
          },

          '&.Mui-disabled + .MuiSwitch-track': {
            opacity: 0.6,
          },
        },
        '& .MuiSwitch-track': {
          background: paletteOf(theme).grey['400'],

          borderRadius: 25 / 2,
          border: 0,
        },
        '& .MuiSwitch-thumb': {
          width: 18,
          height: 18,
        },
      }),
      sizeMedium: {
        height: 30,
        width: 50,
        padding: 0,
      },
    },
  },
  MuiAutocomplete: {
    defaultProps: {
      popupIcon: <ExpandMoreRounded />,
    },
    styleOverrides: {
      paper: ({ theme }) => ({
        boxShadow: theme.shadows[4],
        padding: theme.spacing(2),
        borderRadius: Number(theme.shape.borderRadius) * 3,
        border: `1px solid ${paletteOf(theme).grey.A100}`,
      }),
      listbox: {
        padding: 0,
      },
      option: ({ theme }) => ({
        ...theme.typography.body2,
        fontWeight: 500,
        padding: theme.spacing(1) + '!important',
        borderRadius: Number(theme.shape.borderRadius) * 3,
        '&.Mui-selected': {
          background: listHighlight(theme, 'selected'),
          '&.Mui-focusVisible': {
            background: listHighlight(theme, 'selected'),
          },
        },
        '&:hover, &:focus, &.Mui-focused': {
          background: listHighlight(theme, 'hover'),
        },
        '&.Mui-disabled': {
          background: 'transparent',
        },
      }),
      groupLabel: {
        paddingLeft: 0,
      },
    },
  },
  MuiLink: {
    styleOverrides: {
      root: () => ({
        letterSpacing: 'inherit',
      }),
    },
  },
  MuiAlert: {
    styleOverrides: {
      standardInfo: ({ theme }) => ({
        '.MuiAlert-icon': { color: darkened(theme, paletteOf(theme).info.light, 0.6) },
      }),
    },
  },
};
